import React from 'react';
import { useData } from '../../context/DataContext';
import { levelLabel } from '../../utils/calculations';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

export const QuestionPerformanceChart = ({
  selectedGrade,
  selectedSubject,
  onQuestionClick
}) => {
  const { getScopedStudents, englishResults, mathResults, loMap, levelMap } = useData();

  const scoped = getScopedStudents();

  // 1. Calculate question performance percentages
  const engStats = Array(20).fill(0).map(() => ({ correct: 0, total: 0 }));
  const mathStats = Array(20).fill(0).map(() => ({ correct: 0, total: 0 }));

  scoped.forEach(s => {
    if (!s.participated) return;
    if (selectedGrade !== 'all' && s.registeredGrade !== selectedGrade) return;

    const addStats = (resultsMap, stats) => {
      const data = resultsMap.get(s.id);
      if (data) {
        data.answers.forEach((ans, idx) => {
          if (idx < 20 && !isNaN(ans)) {
            stats[idx].total++;
            if (ans === 1) stats[idx].correct++;
          }
        });
      }
    };

    addStats(englishResults, engStats);
    addStats(mathResults, mathStats);
  });

  const engPcts = engStats.map(q => (q.total > 0 ? ((q.correct / q.total) * 100).toFixed(1) : 0));
  const mathPcts = mathStats.map(q => (q.total > 0 ? ((q.correct / q.total) * 100).toFixed(1) : 0));

  // 2. Compute Level Groups for bottom brackets
  const computeLevelGroups = () => {
    if (selectedGrade === 'all') return null;
    const preferredSubject = selectedSubject === 'math' ? 'Math' : 'English';
    const otherSubject = preferredSubject === 'Math' ? 'English' : 'Math';

    const levelsArr = [];
    for (let qi = 1; qi <= 20; qi++) {
      let lvl = levelMap.get(`${preferredSubject}-${selectedGrade}-${qi}`);
      if (lvl === undefined) {
        lvl = levelMap.get(`${otherSubject}-${selectedGrade}-${qi}`);
      }
      levelsArr.push(lvl);
    }
    
    if (levelsArr.every(l => l === undefined)) return null;

    const groups = [];
    let start = 0;
    for (let i = 1; i <= levelsArr.length; i++) {
      if (i === levelsArr.length || levelsArr[i] !== levelsArr[start]) {
        if (levelsArr[start] !== undefined) {
          groups.push({
            startIdx: start,
            endIdx: i - 1,
            label: levelLabel(levelsArr[start])
          });
        }
        start = i;
      }
    }
    return groups;
  };

  const levelGroups = computeLevelGroups();

  // Custom brackets plugin
  const levelBracketsPlugin = {
    id: 'levelBrackets',
    afterDraw(chart) {
      const groups = chart.config.options.plugins.levelGroups;
      if (!groups || groups.length === 0) return;
      const { ctx, scales: { x } } = chart;
      if (!x) return;
      const slotWidth = x.getPixelForValue(1) - x.getPixelForValue(0);
      const half = slotWidth / 2;
      const y = x.bottom + 16;
      const tick = 5;

      ctx.save();
      ctx.strokeStyle = '#9333ea';
      ctx.fillStyle = '#9333ea';
      ctx.lineWidth = 1.5;
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      groups.forEach(g => {
        const xStart = x.getPixelForValue(g.startIdx) - half + 2;
        const xEnd = x.getPixelForValue(g.endIdx) + half - 2;

        ctx.beginPath();
        ctx.moveTo(xStart, y - tick);
        ctx.lineTo(xStart, y);
        ctx.lineTo(xEnd, y);
        ctx.lineTo(xEnd, y - tick);
        ctx.stroke();

        ctx.fillText(g.label, (xStart + xEnd) / 2, y + 3);
      });
      ctx.restore();
    }
  };

  const isSingleSubject = selectedSubject === 'english' || selectedSubject === 'math';
  const activeSubjectName = selectedSubject === 'math' ? 'Mathematics' : 'English';
  const activePcts = selectedSubject === 'math' ? mathPcts : engPcts;

  let datasets = [];
  if (selectedSubject === 'all') {
    datasets = [
      {
        label: 'English',
        data: engPcts,
        backgroundColor: '#8b5cf6',
        borderRadius: 4
      },
      {
        label: 'Math',
        data: mathPcts,
        backgroundColor: '#3b82f6',
        borderRadius: 4
      }
    ];
  } else {
    datasets = [
      {
        label: `${activeSubjectName} Correct %`,
        data: activePcts,
        backgroundColor: '#8b5cf6',
        borderRadius: 4
      }
    ];
  }

  const chartData = {
    labels: Array.from({ length: 20 }, (_, i) => `Q${i + 1}`),
    datasets
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 25,
        bottom: selectedGrade !== 'all' ? 34 : 10
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: { color: '#f3f4f6' },
        ticks: { color: '#6b7280', font: { size: 10 } }
      },
      x: {
        grid: { display: false },
        ticks: { color: '#374151', font: { size: 10, weight: 'bold' } }
      }
    },
    plugins: {
      levelGroups: levelGroups,
      legend: {
        display: selectedSubject === 'all',
        position: 'top',
        labels: {
          color: '#4b5563',
          font: { weight: 'bold', size: 11 }
        }
      },
      datalabels: {
        color: '#4b5563',
        anchor: 'end',
        align: 'top',
        formatter: (val) => `${Math.round(val)}%`,
        font: { weight: 'bold', size: 9 }
      },
      tooltip: {
        callbacks: {
          label: (c) => {
            const subj = selectedSubject === 'all' ? c.dataset.label : activeSubjectName;
            const qNum = c.dataIndex + 1;
            const lines = [`${subj} Avg Correct: ${c.parsed.y}%`];
            
            if (selectedGrade !== 'all') {
              const loName = loMap.get(`${subj}-${selectedGrade}-${qNum}`);
              if (loName) {
                lines.push(`LO: ${loName}`);
              }
            }
            return lines;
          }
        }
      }
    },
    onClick: (e, activeEls) => {
      if (activeEls.length > 0) {
        const index = activeEls[0].index;
        onQuestionClick(index + 1);
      }
    }
  };

  return (
    <div className="chart-wrapper-tall mb-6" style={{ position: 'relative', height: '360px' }}>
      <Bar
        data={chartData}
        options={chartOptions}
        plugins={[levelBracketsPlugin]}
      />
    </div>
  );
};
export default QuestionPerformanceChart;
